import React, { useState } from 'react';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, PageBreak } from 'docx';
import { saveAs } from 'file-saver';
import { AiOutlineFileText, AiOutlineCheck, AiOutlineWarning } from 'react-icons/ai';

const DeclarationModule = ({ rowData, extraData }) => {
    const [fields, setFields] = useState({
        gstin: "",
        fromPeriod: "",
        toPeriod: "",
        amount: "",
        partnerName: "",
        designation: "Partner",
        place: "",
        address: "",
        applicantName: ""
    });

    React.useEffect(() => {
        if (rowData?.client) {
            setFields(prev => ({
                ...prev,
                gstin: rowData.client.gstin_no || "",
                place: rowData.client.city || "",
                applicantName: rowData.client.firm_name || "",
                address: [rowData.client.address_line_one, rowData.client.address_line_two, rowData.client.city, rowData.client.state, rowData.client.pincode].filter(Boolean).join(", ") || "",
                partnerName: rowData.client.person_one_name || "",
                designation: extraData?.designation || "Partner",
                amount: extraData?.refundAfterUpload || "",
                fromPeriod: extraData?.from ? `${String(extraData.from.getMonth() + 1).padStart(2, '0')}/${extraData.from.getFullYear()}` : "",
                toPeriod: extraData?.to ? `${String(extraData.to.getMonth() + 1).padStart(2, '0')}/${extraData.to.getFullYear()}` : ""
            }));
        }
    }, [rowData, extraData]);



    const [errors, setErrors] = useState([]);
    const [success, setSuccess] = useState(false);

    // Global Helpers
    function amountInWords(amount) {
        const num = Math.round(Math.abs(parseFloat(amount) || 0));
        if (num === 0) return "Rupees Zero Only";

        const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
            "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
            "Seventeen", "Eighteen", "Nineteen"];
        const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

        function convertHundreds(n) {
            let str = "";
            if (n > 99) {
                str += ones[Math.floor(n / 100)] + " Hundred ";
                n = n % 100;
            }
            if (n > 19) {
                str += tens[Math.floor(n / 10)] + " ";
                n = n % 10;
            }
            if (n > 0) str += ones[n] + " ";
            return str.trim();
        }

        let result = "";
        const crore = Math.floor(num / 10000000);
        const lakh = Math.floor((num % 10000000) / 100000);
        const thousand = Math.floor((num % 100000) / 1000);
        const remainder = num % 1000;

        if (crore > 0) result += convertHundreds(crore) + " Crore ";
        if (lakh > 0) result += convertHundreds(lakh) + " Lakh ";
        if (thousand > 0) result += convertHundreds(thousand) + " Thousand ";
        if (remainder > 0) result += convertHundreds(remainder);

        return "Rupees " + result.trim() + " Only";
    }

    function formatPeriod(fromFp, toFp) {
        const months = ["", "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"];
        try {
            const [fm, fy] = fromFp.split("/");
            const [tm, ty] = toFp.split("/");
            const fromStr = (months[parseInt(fm)] || fm) + " " + fy;
            const toStr = (months[parseInt(tm)] || tm) + " " + ty;
            return fromStr + " to " + toStr;
        } catch {
            return fromFp + (toFp ? " to " + toFp : "");
        }
    }

    function todayDDMMYYYY() {
        const d = new Date();
        return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    }

    const validate = () => {
        const required = ['gstin', 'fromPeriod', 'toPeriod', 'amount', 'partnerName', 'applicantName'];
        const missing = required.filter(f => !fields[f]);
        if (missing.length > 0) {
            setErrors(missing.map(f => f.charAt(0).toUpperCase() + f.slice(1).replace(/([A-Z])/g, ' $1')));
            return false;
        }
        setErrors([]);
        return true;
    };

    const handleGenerate = async () => {
        if (!validate()) return;

        const {
            gstin, fromPeriod, toPeriod, amount, partnerName,
            designation = "Partner", place, applicantName, address
        } = fields;

        const amtWords = amountInWords(amount);
        const period = formatPeriod(fromPeriod, toPeriod);
        const today = todayDDMMYYYY();
        const amtFormatted = parseFloat(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        });

        const divider = () => new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: "__________________________________________________________________________", bold: true, size: 20 })]
        });

        // Helper: blank line
        const blank = () => new Paragraph({ children: [new TextRun("")] });

        // Helper: centered bold paragraph
        const centeredBold = (text, size = 24) => new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text, bold: true, size })]
        });

        // Helper: centered normal
        const centeredNormal = (text, italic = false) => new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text, italic, size: 22 })]
        });

        // Helper: left paragraph (Justified for that 'perfect' legal look)
        const bodyPara = (text, bold = false) => new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { line: 360, before: 200, after: 200 },
            children: [new TextRun({ text, size: 22, bold, font: "Calibri" })]
        });

        // Helper: signature line
        const sigLine = (label, value) => new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [
                new TextRun({ text: `${label.padEnd(20, " ")}`, bold: true, size: 22 }),
                new TextRun({ text: value, size: 22 })
            ]
        });

        // Type 1 Header (SANJA & CO)
        const getCAHeader = () => [
            centeredBold("SANJA & CO", 28),
            centeredNormal("3rd Floor, Galaxy Plaza, Sanala Road, Morbi 363641"),
            centeredNormal("M: 8140200062"),
            divider(),
            blank(),
        ];

        // Type 2 Header (M/s Applicant)
        const getCompanyHeader = () => {
            const elements = [
                centeredBold(applicantName, 28)
            ];
            if (address) {
                elements.push(centeredNormal(address));
            }
            if (rowData?.client?.person_one_no) {
                elements.push(centeredNormal(`M: ${rowData.client.person_one_no}`));
            }
            elements.push(divider());
            elements.push(blank());
            return elements;
        };

        // Info Block (Pages 2,3,4)
        const getInfoBlock = () => [
            blank(),
            sigLine("Applicant:", `M/s ${applicantName}`),
            sigLine("Refund Amount:", `Rs. ${amtFormatted} (In words: ${amtWords})`),
            sigLine("Period:", period),
        ];

        // Signature Block — Partner (Pages 2,3,4)
        const getSignaturePartner = () => [
            blank(),
            bodyPara(`For, ${applicantName}`, true),
            blank(),
            new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                    new TextRun({
                        text: partnerName ? `  ${partnerName}  ` : "   ",
                        font: "Brush Script MT",
                        size: 36,
                        italic: true,
                        color: "1A365D"
                    })
                ]
            }),
            blank(),
            bodyPara("Authorised Signatory / Partner"),
            blank(),
            sigLine("Name:", partnerName),
            sigLine("Designation:", designation),
            sigLine("Date:", today),
            sigLine("Place:", place),
        ];

        // Signature Block — SANJA & CO (Page 1)
        const getSignatureCA = () => [
            blank(),
            bodyPara(`For, SANJA & CO`, true),
            new Paragraph({ children: [new TextRun({ text: "Chartered Accountant", size: 22 })] }),
            blank(),
            new Paragraph({
                alignment: AlignmentType.LEFT,
                children: [
                    new TextRun({
                        text: "  Pintu Sanja  ",
                        font: "Brush Script MT",
                        size: 36,
                        italic: true,
                        color: "1A365D"
                    })
                ]
            }),
            blank(),
            new Paragraph({ children: [new TextRun({ text: "Pintu Sanja", bold: true, size: 22 })] }),
            new Paragraph({ children: [new TextRun({ text: "Proprietor", size: 22 })] }),
            new Paragraph({ children: [new TextRun({ text: "M.No. 542368", size: 22 })] }),
            new Paragraph({ children: [new TextRun({ text: "UDIN: [Insert UDIN Here]", size: 22 })] }),
        ];

        // PAGE 1 — CA Certificate
        const page1 = [
            ...getCAHeader(),
            new Paragraph({
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ text: "Certificate [rule 89(2)(m) of CGST Rules 2017]", bold: true })]
            }),
            blank(),
            bodyPara(`This is to certify that in respect of the refund amounting to Rs.${amtFormatted} (In words: ${amtWords}) claimed by M/s ${applicantName} GSTIN: ${gstin} Temporary ID: ${gstin} for the tax period ${period}, the incidence of tax and interest has not been passed on to any other person.`),
            bodyPara("This certificate is based on the examination of the books of account and other relevant records and returns particulars maintained/furnished by the applicant."),
            ...getSignatureCA(),
        ];

        // Helper: Declaration page builder
        const buildDeclarationPage = (title, subtitle, bodyText) => [
            ...getCompanyHeader(),
            new Paragraph({
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                children: [new TextRun({ text: title, bold: true })]
            }),
            centeredNormal(subtitle, true),
            blank(),
            bodyPara(bodyText),
            ...getInfoBlock(),
            ...getSignaturePartner(),
        ];

        // PAGE 2 — Declaration 1
        const page2 = buildDeclarationPage(
            "DECLARATION",
            "(Under Second Proviso to Section 54(3) of the CGST Act, 2017)",
            "I hereby declare that the goods exported are not subject to any export duty. I further declare that I have not availed any drawback on goods or services or both, and that I have not claimed a refund of the integrated tax paid on supplies in respect of which refund is claimed."
        );

        // PAGE 3 — Declaration 2
        const page3 = buildDeclarationPage(
            "DECLARATION",
            "(Under Section 54(3)(ii) of the CGST Act, 2017)",
            "I hereby declare that the refund of input tax credit claimed in the application does not include ITC availed on goods or services used for making 'nil' rated or fully exempt supplies."
        );

        // PAGE 4 — Self-Declaration
        const page4 = buildDeclarationPage(
            "SELF-DECLARATION",
            "(Under Rule 89(2)(l) of the CGST Rules, 2017)",
            `I, ${partnerName} Partner of ${applicantName}, having GSTIN ${gstin}, solemnly affirm and certify that in respect of the refund amounting to Rs. ${amtFormatted} (In words: ${amtWords}), with respect to the tax, interest, or any other amount for the period ${period}, claimed in the refund application, the incidence of such tax and interest has not been passed on to any other person.`
        );

        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
                    }
                },
                children: [
                    ...page1,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...page2,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...page3,
                    new Paragraph({ children: [new PageBreak()] }),
                    ...page4,
                ]
            }]
        });

        const blob = await Packer.toBlob(doc);
        saveAs(blob, `Declarations_${applicantName}_${today}.docx`);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
    };

    return (
        <div className="declaration-module">
            <style>{`
                .declaration-module {
                    background: #ffffff;
                    color: #1e293b;
                    padding: 24px;
                    border-radius: 12px;
                    font-family: 'DM Mono', monospace;
                }
                .syne { font-family: 'Syne', sans-serif; }
                .form-panel {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    padding: 24px;
                    border-radius: 12px;
                    max-width: 800px;
                    margin: 0 auto;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-bottom: 24px;
                }
                .form-item { display: flex; flex-direction: column; gap: 6px; }
                .form-item.full { grid-column: span 2; }
                label { color: #64748b; font-size: 13px; }
                label span { color: #d97706; margin-left: 2px; }
                input {
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    color: #1e293b;
                    padding: 0px 14px;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: border-color 0.3s;
                }
                input:focus { border-color: #d97706; outline: none; }
                .preview-text { font-size: 12px; color: #d97706; margin-top: 4px; }
                .btn-generate {
                    background: #d97706;
                    color: #ffffff;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 8px;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    justify-content: center;
                    transition: all 0.3s;
                }
                .btn-generate:hover { box-shadow: 0 0 20px rgba(217, 119, 6, 0.3); transform: translateY(-1px); }
                .error-alert {
                    background: rgba(251, 113, 133, 0.08);
                    border: 1px solid #fb7185;
                    color: #be123c;
                    padding: 12px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                    font-size: 13px;
                }
                .success-card {
                    margin-top: 24px;
                    background: rgba(52, 211, 153, 0.08);
                    border: 1px solid #34d399;
                    border-radius: 8px;
                    padding: 16px;
                }
                .summary-list { margin-top: 10px; font-size: 12px; color: #64748b; }
                .summary-list li { margin-bottom: 4px; }
            `}</style>

            <div className="form-panel">
                <h3 className="syne" style={{ margin: '0 0 24px 0', display: 'flex', alignItems: 'center', gap: '12px', color: '#1e293b' }}>
                    <AiOutlineFileText color="#d97706" /> GST Declaration Generator
                </h3>

                {errors.length > 0 && (
                    <div className="error-alert">
                        <AiOutlineWarning style={{ marginRight: '8px' }} />
                        Please fill required fields: {errors.join(", ")}
                    </div>
                )}

                <div className="form-grid">
                    <div className="form-item full">
                        <label>Applicant Name<span>*</span></label>
                        <input value={fields.applicantName} onChange={e => setFields({ ...fields, applicantName: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>GSTIN<span>*</span></label>
                        <input value={fields.gstin} onChange={e => setFields({ ...fields, gstin: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>Refund Amount (₹)<span>*</span></label>
                        <input type="number" value={fields.amount} onChange={e => setFields({ ...fields, amount: e.target.value })} />
                        {fields.amount && <div className="preview-text">{amountInWords(fields.amount)}</div>}
                    </div>
                    <div className="form-item">
                        <label>From Period<span>*</span> (mm/yyyy)</label>
                        <input placeholder="04/2023" value={fields.fromPeriod} onChange={e => setFields({ ...fields, fromPeriod: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>To Period<span>*</span> (mm/yyyy)</label>
                        <input placeholder="03/2024" value={fields.toPeriod} onChange={e => setFields({ ...fields, toPeriod: e.target.value })} />
                        {fields.fromPeriod && fields.toPeriod && <div className="preview-text">{formatPeriod(fields.fromPeriod, fields.toPeriod)}</div>}
                    </div>
                    <div className="form-item">
                        <label>Partner Name<span>*</span></label>
                        <input value={fields.partnerName} onChange={e => setFields({ ...fields, partnerName: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>Designation</label>
                        <input placeholder="Partner" value={fields.designation} onChange={e => setFields({ ...fields, designation: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>Place<span>*</span></label>
                        <input value={fields.place} onChange={e => setFields({ ...fields, place: e.target.value })} />
                    </div>
                    <div className="form-item">
                        <label>Address</label>
                        <input value={fields.address} onChange={e => setFields({ ...fields, address: e.target.value })} />
                    </div>
                </div>

                <button className="btn-generate" onClick={handleGenerate}>
                    <AiOutlineFileText size={20} /> Generate & Download Declaration
                </button>

                {success && (
                    <div className="success-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#34d399', fontWeight: 'bold' }}>
                            <AiOutlineCheck /> Declaration generated successfully!
                        </div>
                        <ul className="summary-list">
                            <li>├── Page 1: CA Certificate (Rule 89(2)(m))</li>
                            <li>├── Page 2: Declaration (Sec 54(3) 2nd Proviso)</li>
                            <li>├── Page 3: Declaration (Sec 54(3)(ii))</li>
                            <li>└── Page 4: Self-Declaration (Rule 89(2)(l))</li>
                        </ul>
                        <div style={{ fontSize: '11px', marginTop: '8px', color: '#64748b' }}>
                            Refund: ₹{parseFloat(fields.amount).toLocaleString()} | Period: {formatPeriod(fields.fromPeriod, fields.toPeriod)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeclarationModule;
